import React, { useState, useEffect, useMemo } from 'react';

import { dataGridPremiumColumns, generateColumnsFromDynProps } from '../../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi } from '../../Interface';
import complaintCoreService from '../../../service/NewComplaint/complaintCore.service';
import dayjs from 'dayjs';
import useGenerateJokerReportCols from '../../../hooks/useGenerateJokerReportCols';

const ComplaintCoreReports = ({
  refetch,
  values,
  viewField,
  operatorsDisabled,
  rowSelection,
  autocompleteOptions,
  autocompleteData,
  dynProps,
  jokerFields,
}) => {
  const { refetchTable, setRefetchTable } = refetch;
  const { setSelectedValues } = values;
  const { rowSelectionModel, setRowSelectionModel } = rowSelection;

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'id', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const getList = (gridState) => {
    complaintCoreService.getItems(gridState).then((data) => {
      setReports(data);
    });
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'id',
        headerName: '#',
        type: 'string',
        filterable: false,
      },
      {
        field: 'companyId',
        headerName: 'Cég',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.company?.name;
        },
        valueOptions: autocompleteOptions.company,
      },
      {
        field: 'reference',
        headerName: 'Hivatkozás',
        type: 'string',
      },
      {
        field: 'itemCode',
        headerName: 'Cikkszám',
        type: 'string',
        sortable: false,
        renderCell: (data) => {
          return data.row.item?.itemCode;
        },
      },
      {
        field: 'itemName',
        headerName: 'Cikk megnevezés',
        type: 'string',
      },
      {
        field: 'quantity',
        headerName: 'Reklamált mennyiség',
        type: 'number',
        filterable: false,
        sortable: false,
      },
      {
        field: 'date',
        type: 'date',
        headerName: 'Dátum',
        valueGetter: (_data, row) => {
          return new Date(row.date);
        },
        renderCell: (data) => {
          return dayjs(data.row.date).format('YYYY.MM.DD');
        },
      },

      {
        field: 'complaintStatusId',
        headerName: 'Állapot',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.complaintStatus?.name;
        },
        valueOptions: autocompleteOptions.complaintStatus,
      },
      {
        field: 'supplierId',
        headerName: 'Szállító',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.supplier?.companyName;
        },
        valueOptions: autocompleteOptions.company,
      },
      {
        field: 'complaintTypeId',
        headerName: 'Panasz típus',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.complaintType?.name;
        },
        valueOptions: autocompleteOptions.complaintType,
      },
      {
        field: 'operationId',
        headerName: 'Művelet',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.operation?.name;
        },
        valueOptions: autocompleteOptions.operation,
      },
      {
        field: 'complaintReasonId',
        headerName: 'Reklamáció jellege',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.complaintReason?.name;
        },
        valueOptions: autocompleteOptions.complaintReason,
      },
      {
        field: 'complaintResolutionRequestId',
        headerName: 'Rendezési igény',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.complaintResolutionRequest?.name;
        },
        valueOptions: autocompleteOptions.complaintResolutionRequest,
      },

      {
        field: 'complaintResolutionId',
        headerName: 'Reklamáció végkimenetel',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.complaintResolution?.name;
        },
        valueOptions: autocompleteOptions.complaintResolution,
      },
      {
        field: 'complaintActionTakenId',
        headerName: 'Elbírálás eredménye',
        type: 'singleSelect',
        sortable: false,
        renderCell: (data) => {
          return data.row.complaintActionTaken?.name;
        },
        valueOptions: autocompleteOptions.complaintActionTaken,
      },
      ...generateColumnsFromDynProps(dynProps ?? []),
      ...jokerCols,
    ]);
  }, [autocompleteOptions, dynProps, jokerCols]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.ComplaintCore}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
    />
  );
};

export default ComplaintCoreReports;
